import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  ShareIcon,
  TextInput,
  Dialog,
  DialogContent,
  DialogFooter,
  Horizontal,
  Vertical,
  HelpTip,
  Heading,
} from '@taopix/taopix-design-system';
import {useTaopix} from '../Context/TaopixContext';
import {
  ButtonProps,
  buttonSizeTypes,
  buttonStyles,
  labelAlignmentTypes,
} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {UserFlowStep} from '../../Facade/UserFlow/BaseUserFlow';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import classNames from 'classnames';

export type ShareResponsePayload = {
  link: string;
  brandcode: string;
  result: number;
  resultmessage: string;
};

export type ShareButtonProps = {
  onProjectShared?: (payload: ShareResponsePayload) => void;
  onClose?: () => void;
  className?: string;
  showShareProjectButton: boolean;
} & Partial<
  Pick<
    ButtonProps,
    | 'label'
    | 'buttonStyle'
    | 'corners'
    | 'style'
    | 'stacked'
    | 'labelAlignment'
    | 'startIcon'
    | 'size'
  >
>;

const ShareButton = ({
  onProjectShared,
  onClose,
  className,
  showShareProjectButton,
  ...rest
}: ShareButtonProps) => {
  const app = useTaopix();
  const {t} = useTranslation(['shareproject', 'dialogs']);
  const ui = useUiState();
  const userFlow = useUserFlowState();

  const [shareURL, setShareURL] = useState('');
  const [helpTipOpen, setHelpTipOpen] = useState(false);

  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      PubSub.subscribe('SHARE_LINK', (msg, data) => {
        setShareURL(data);
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, []);

  const onShare = () => {
    userFlow.activateUserFlow(UserFlowType.Share);
  };

  const onCloseDialog = () => {
    if (onClose) {
      onClose();
    }
    userFlow.setCurrentStep(`${UserFlowType.Share}.${UserFlowStep.None}`);
  };

  const onCopyLink = (event: React.MouseEvent) => {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      setHelpTipOpen(true);
      return navigator.clipboard.writeText(shareURL);
    }
    return false;
  };

  const buttonProps = {
    label: t('*:str_ButtonShare'),
    buttonStyle: 'standard' as buttonStyles,
    stacked: true,
    size: 'small' as buttonSizeTypes,
    labelAlignment: 'left' as labelAlignmentTypes,
    startIcon: <ShareIcon />,
    ...rest,
  };

  const shareButtonClasses = classNames(!showShareProjectButton && 'hidden', 'shrink-0', className);

  return (
    <>
      <Button
        onClick={onShare}
        labelLines={1}
        className={shareButtonClasses}
        id={'headerShareButton'}
        {...buttonProps}
      />

      <Dialog
        open={userFlow.isStepActive(UserFlowType.Share, UserFlowStep.Share)}
        id={'projectShareDialog'}
        className={'w-[400rem] max-w-bounds'}
        focusFirstElement
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        onClose={onCloseDialog}
        title={t('dialogs:str_TitleShareProject', {defaultValue: 'Share Project'})}
        showTitle={false}
      >
        <DialogContent>
          <div className={'space-y-sm flex-col w-full'}>
            <Heading size={2}>{t('shareproject:str_LabelShareLink')}</Heading>
            <div className="flex space-x-sm">
              <TextInput
                type={'text'}
                id={'url'}
                required={true}
                name={'url'}
                value={shareURL}
                className="flex-1 w-full"
                containerClassName={'flex-1'}
                disabled
              />
              <Button
                size="small"
                buttonStyle="primary"
                label={t('shareproject:str_ButtonCopyLink')}
                onClick={onCopyLink}
              />
            </div>
          </div>
        </DialogContent>
        <DialogFooter isSmallScreen={false} alignment={ui.state.isSmallScreen ? 'auto' : 'left'}>
          <Button buttonStyle="negative" label={t('*:str_ButtonCancel')} onClick={onCloseDialog} />
        </DialogFooter>
      </Dialog>

      <HelpTip
        open={helpTipOpen}
        message={t('*:str_ToolTipLinkCopied')}
        anchorId={'url'}
        anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Centre}}
        transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Centre}}
        offset={{x: 0, y: 5}}
        className={'max-w-[300rem]'}
        buttonFunction={() => setHelpTipOpen(false)}
        onClose={() => setHelpTipOpen(false)}
        excludeIdFromClickOutside={'url'}
        arrow
        repositionOnResize
      />
    </>
  );
};

export default ShareButton;
